import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dimmer,
  Dropdown,
  Grid,
  Icon,
  Input,
  Loader,
  Modal,
  Tab
} from 'semantic-ui-react';
import styled from 'styled-components';
import { getEstate } from '../../../services/estate';
import { EstateForGetEstatesResponseDTO } from '../../../dtos/estate/estate-for-get-estates-response.dto';
import { maskCpf, maskCpfOrCnpj, maskPhoneNumber } from '../../../services/masks';
import { DateToStringDate } from '../../../services/date';
import { BankOptions, getEstateBankByCode } from '../../../resources/bankCodeList';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../services/notification';
import { put } from '../../../services/request';
import { isValidEmail } from '../../../utils/emailValidator';
import {
  InsurerGrid,
  insurerGridStyles
} from '../../../pages/estates-list/components/ViewModal/styles';
import IconButton from '../../../components/IconButton';
import { useHistory } from 'react-router-dom';
import { useStandaloneChargeAccountStatus } from '../../../pages/standalone-charges/hooks/useStandaloneChargeAccountStatus';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Section = styled.div`
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SectionTitle = styled.h4`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #59249b;
  margin: 0;
`;

const SectionDivider = styled.div`
  height: 1px;
  background: #e8e8e8;
  margin-bottom: 20px;
`;

const FieldGrid = styled.div<{ cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols ?? 3}, 1fr);
  gap: 20px 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const FieldLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #aaadb1;
  white-space: nowrap;
`;

const FieldValue = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #393939;
  word-break: break-word;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #aaadb1;
  margin: 0;
  padding: 8px 0;
`;

const PartnerCard = styled.div`
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px 16px;
  flex-wrap: wrap;
`;

const EmailAddress = styled.span`
  font-size: 14px;
  color: #393939;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 220px;

  .icon {
    color: #59249b !important;
    margin: 0 !important;
  }
`;

const CategoryTagsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: auto;
`;

const CategoryTag = styled.span<{ color: string; bg: string }>`
  display: inline-flex;
  align-items: center;
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const Disclaimer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 14px 18px;
  margin-top: 20px;

  .icon {
    font-size: 1.3rem !important;
    color: #f59e0b !important;
    flex-shrink: 0;
    margin-top: 1px;
  }

  p {
    font-size: 13px;
    color: #78600a;
    margin: 0;
    line-height: 1.5;
  }
`;

const TabContent = styled.div`
  padding: 24px 4px 8px;
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px solid #59249b;
  color: #59249b;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #59249b;
    color: #fff;
  }

  i.icon {
    margin: 0 !important;
    font-size: 12px !important;
  }
`;

const EditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
`;

const EditEmailRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 8px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const MainEmailField = styled.div`
  margin-bottom: 20px;
  padding: 14px 16px;
  background: #f3eefe;
  border: 1px solid #d4b8fa;
  border-radius: 8px;
`;

const MainEmailLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #59249b;
  margin-bottom: 8px;
`;

const AddEmailButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px dashed #aaadb1;
  color: #59249b;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  margin-top: 8px;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #59249b;
    background: #f9f5ff;
  }

  i.icon {
    margin: 0 !important;
  }
`;

const SubSectionLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #888;
  margin: 0 0 10px 0;
`;

// ---------------------------------------------------------------------------

interface DadosCadastraisModalProps {
  isOpen: boolean;
  onClose: () => void;
  estateId: string;
  canViewBillingConfiguration: boolean;
  canManageBillingConfiguration: boolean;
  canEditOperationFee: boolean;
}

interface NotificationEmailObj {
  id?: string;
  email: string;
  category?: string;
  subcategory?: string;
}

interface GroupedEmail {
  email: string;
  categories: string[];
}

interface AdditionalEmailItem {
  email: string;
  categories: string[];
}

interface AddressForm {
  estate_zipcode: string;
  estate_street: string;
  estate_district: string;
  estate_city: string;
  estate_state: string;
  estate_number: string;
  estate_complement: string;
}

interface BillingForm {
  pix_key_type: string;
  pix_key: string;
  standalone_charges_split_percent: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  CLAIMS: 'Sinistros',
  ANALYSIS: 'Análise',
  HIRING: 'Contratação',
  CANCELEDS: 'Cancelamentos',
  RENEWALS: 'Renovações',
  FIRE_INSURANCE: 'Seg. Incêndio',
  CAPITALIZATIONS: 'Capitalização'
};
const DEFAULT_CATEGORY_COLOR = { color: '#7c3aed', bg: '#ede9fe' };

const CATEGORY_OPTIONS = [
  { key: 'ANALYSIS', text: 'Análise', value: 'ANALYSIS' },
  { key: 'HIRING', text: 'Contratação', value: 'HIRING' },
  { key: 'CANCELEDS', text: 'Cancelamentos', value: 'CANCELEDS' },
  { key: 'CLAIMS', text: 'Sinistros', value: 'CLAIMS' },
  { key: 'RENEWALS', text: 'Renovações', value: 'RENEWALS' },
  { key: 'FIRE_INSURANCE', text: 'Seg. Incêndio', value: 'FIRE_INSURANCE' }
];

const VALID_CATEGORIES = CATEGORY_OPTIONS.map(c => c.value);
const PIX_KEY_TYPE_OPTIONS = [
  { key: 'CPF', text: 'CPF', value: 'CPF' },
  { key: 'CNPJ', text: 'CNPJ', value: 'CNPJ' },
  { key: 'EMAIL', text: 'E-mail', value: 'EMAIL' },
  { key: 'PHONE', text: 'Telefone', value: 'PHONE' },
  { key: 'EVP', text: 'Chave Aleatória', value: 'EVP' }
];

interface InsurerFormalized {
  id: string;
  name: string;
  image?: string;
  hasCapitalization?: boolean;
  hasFireInsurance?: boolean;
  hasInsurance?: boolean;
}

const emptyAddress = (): AddressForm => ({
  estate_zipcode: '',
  estate_street: '',
  estate_district: '',
  estate_city: '',
  estate_state: '',
  estate_number: '',
  estate_complement: ''
});

const emptyBilling = (): BillingForm => ({
  pix_key_type: '',
  pix_key: '',
  standalone_charges_split_percent: ''
});

const DadosCadastraisModal: React.FC<DadosCadastraisModalProps> = ({
  isOpen,
  onClose,
  estateId,
  canViewBillingConfiguration,
  canManageBillingConfiguration,
  canEditOperationFee
}) => {
  const history = useHistory();
  const [estate, setEstate] = useState<EstateForGetEstatesResponseDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [bankSelected, setBankSelected] = useState<BankOptions | null>(null);
  const [insurersDataFormalized, setInsurersDataFormalized] = useState<
    InsurerFormalized[]
  >([]);

  // --- address edit state ---
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState<AddressForm>(emptyAddress());
  const [savingAddress, setSavingAddress] = useState(false);
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingForm, setBillingForm] = useState<BillingForm>(emptyBilling());
  const [savingBilling, setSavingBilling] = useState(false);
  const [savingBillingSettings, setSavingBillingSettings] = useState(false);
  const chargesEnabled = Boolean(estate?.standalone_charges_enabled);
  const { connected, loading: accountLoading } = useStandaloneChargeAccountStatus(
    estateId,
    chargesEnabled
  );

  // --- emails edit state ---
  const [editingEmails, setEditingEmails] = useState(false);
  const [mainEmailForm, setMainEmailForm] = useState('');
  const [emailsForm, setEmailsForm] = useState<AdditionalEmailItem[]>([]);
  const [savingEmails, setSavingEmails] = useState(false);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchEstate = async () => {
    setLoading(true);
    try {
      const data = await getEstate(estateId);
      setEstate(data);
    } catch {
      dangerNotification('Erro', 'Não foi possível carregar os dados cadastrais.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !estateId) return;
    fetchEstate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, estateId]);

  // Sync address form when estate loads
  useEffect(() => {
    if (!estate) return;
    setAddressForm({
      estate_zipcode: estate.estate_zip_code || '',
      estate_street: estate.estate_street || '',
      estate_district: estate.estate_district || '',
      estate_city: estate.estate_city || '',
      estate_state: estate.estate_state || '',
      estate_number: estate.estate_number || '',
      estate_complement: estate.estate_complement || ''
    });
    setMainEmailForm(estate.email || '');
    setBillingForm({
      pix_key_type: estate.bank_account?.pix_key_type || '',
      pix_key: estate.bank_account?.pix_key || '',
      standalone_charges_split_percent: String(
        estate.standalone_charges_split_percent ?? ''
      )
    });
  }, [estate]);

  useEffect(() => {
    if (!estate) return;

    const insurersFormalized: InsurerFormalized[] = [];

    estate.insurers?.forEach(insurer => {
      const found = insurersFormalized.find(ins => ins.id === insurer.id);
      if (found) found.hasInsurance = true;
      else insurersFormalized.push({ ...insurer, hasInsurance: true });
    });

    estate.fire_insurers?.forEach(insurer => {
      const found = insurersFormalized.find(ins => ins.id === insurer.id);
      if (found) found.hasFireInsurance = true;
      else insurersFormalized.push({ ...insurer, hasFireInsurance: true });
    });

    estate.capitalizations?.forEach(cap => {
      const found = insurersFormalized.find(ins => ins.id === cap.id);
      if (found) found.hasCapitalization = true;
      else insurersFormalized.push({ ...cap, hasCapitalization: true });
    });

    setInsurersDataFormalized(insurersFormalized);
  }, [estate]);

  useEffect(() => {
    if (!estate?.bank_account?.bank_code) return;

    const fetchBank = async () => {
      try {
        const bank = await getEstateBankByCode(estate.bank_account!.bank_code);
        setBankSelected(bank);
      } catch {
        dangerNotification('Erro', 'Erro ao carregar dados bancários.');
      }
    };

    fetchBank();
  }, [estate?.bank_account?.bank_code]);

  // ---------------------------------------------------------------------------
  // Grouped emails (view mode)
  // ---------------------------------------------------------------------------

  const groupedEmails: GroupedEmail[] = React.useMemo(() => {
    const map = new Map<string, Set<string>>();

    const addEmail = (email: string, category?: string) => {
      const key = email.trim().toLowerCase();
      if (!key) return;
      if (!map.has(key)) map.set(key, new Set());
      if (category) map.get(key)!.add(category);
    };

    if (estate?.notification_emails) {
      if (Array.isArray(estate.notification_emails)) {
        (estate.notification_emails as NotificationEmailObj[]).forEach(item => {
          if (typeof item === 'string') addEmail(item);
          else if (item?.email) addEmail(item.email, item.category);
        });
      } else if (typeof estate.notification_emails === 'string') {
        (estate.notification_emails as string).split(';').forEach(e => addEmail(e));
      }
    }

    if (estate?.additional_emails) {
      estate.additional_emails.split(';').forEach(e => addEmail(e));
    }

    return Array.from(map.entries()).map(([email, cats]) => ({
      email,
      categories: Array.from(cats)
    }));
  }, [estate?.notification_emails, estate?.additional_emails]);

  // ---------------------------------------------------------------------------
  // Edit: address
  // ---------------------------------------------------------------------------

  const handleStartEditAddress = () => {
    if (!estate) return;
    setAddressForm({
      estate_zipcode: estate.estate_zip_code || '',
      estate_street: estate.estate_street || '',
      estate_district: estate.estate_district || '',
      estate_city: estate.estate_city || '',
      estate_state: estate.estate_state || '',
      estate_number: estate.estate_number || '',
      estate_complement: estate.estate_complement || ''
    });
    setEditingAddress(true);
  };

  const handleCancelEditAddress = () => {
    setEditingAddress(false);
  };

  const handleSaveAddress = async () => {
    setSavingAddress(true);
    try {
      await put(`/estates/update-address/${estateId}`, addressForm);
      successNotification('Sucesso!', 'Endereço atualizado com sucesso!');
      setEditingAddress(false);
      await fetchEstate();
    } catch {
      dangerNotification('Erro!', 'Não foi possível atualizar o endereço.');
    } finally {
      setSavingAddress(false);
    }
  };

  const updateAddressField = (field: keyof AddressForm, value: string) => {
    setAddressForm(prev => ({ ...prev, [field]: value }));
  };

  const handleStartEditBilling = () => {
    setBillingForm({
      pix_key_type: estate?.bank_account?.pix_key_type || '',
      pix_key: estate?.bank_account?.pix_key || '',
      standalone_charges_split_percent: String(
        estate?.standalone_charges_split_percent ?? ''
      )
    });
    setEditingBilling(true);
  };

  const handleCancelEditBilling = () => {
    setEditingBilling(false);
  };

  const handleSaveBilling = async () => {
    setSavingBilling(true);
    try {
      await put(`/estates/update-bank-account-billing/${estateId}`, {
        pix_key_type: billingForm.pix_key_type,
        pix_key: billingForm.pix_key,
        ...(canEditOperationFee
          ? {
              standalone_charges_split_percent:
                billingForm.standalone_charges_split_percent === ''
                  ? undefined
                  : Number(billingForm.standalone_charges_split_percent)
            }
          : {})
      });
      successNotification('Sucesso!', 'Dados de cobrança atualizados com sucesso!');
      setEditingBilling(false);
      await fetchEstate();
    } catch {
      dangerNotification('Erro!', 'Não foi possível atualizar os dados de cobrança.');
    } finally {
      setSavingBilling(false);
    }
  };

  const updateBillingField = (field: keyof BillingForm, value: string) => {
    setBillingForm(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateBillingSettings = async (
    standaloneChargesEnabled: boolean,
    standaloneChargesCommissionEnabled: boolean
  ) => {
    setSavingBillingSettings(true);
    try {
      await put(`/estates/update-bank-account-billing/${estateId}`, {
        standalone_charges_enabled: standaloneChargesEnabled,
        standalone_charges_commission_enabled:
          standaloneChargesEnabled && standaloneChargesCommissionEnabled
      });
      setEstate(current =>
        current
          ? {
              ...current,
              standalone_charges_enabled: standaloneChargesEnabled,
              standalone_charges_commission_enabled:
                standaloneChargesEnabled && standaloneChargesCommissionEnabled
            }
          : current
      );
      successNotification('Sucesso!', 'Configurações de cobrança atualizadas!');
    } catch {
      dangerNotification('Erro!', 'Não foi possível atualizar as configurações.');
    } finally {
      setSavingBillingSettings(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Edit: emails
  // ---------------------------------------------------------------------------

  const handleStartEditEmails = () => {
    setMainEmailForm(estate?.email || '');
    const grouped: Record<string, string[]> = {};
    if (estate?.notification_emails && Array.isArray(estate.notification_emails)) {
      (estate.notification_emails as NotificationEmailObj[]).forEach(item => {
        if (!item?.email) return;
        if (['MAIN', 'FINANCIAL', 'RESPONSIBLE'].includes(item.category || '')) return;
        if (!VALID_CATEGORIES.includes(item.category || '')) return;
        const key = item.email.toLowerCase();
        if (!grouped[key]) grouped[key] = [];
        if (item.category) grouped[key].push(item.category);
      });
    }
    const items: AdditionalEmailItem[] = Object.entries(grouped).map(
      ([email, categories]) => ({ email, categories })
    );
    setEmailsForm(items);
    setEditingEmails(true);
  };

  const handleCancelEditEmails = () => {
    setEditingEmails(false);
  };

  const handleSaveEmails = async () => {
    if (!mainEmailForm.trim()) {
      warningNotification('Atenção!', 'O E-mail Principal é obrigatório.');
      return;
    }
    if (!isValidEmail(mainEmailForm.trim())) {
      warningNotification('Atenção!', 'E-mail Principal inválido: ' + mainEmailForm);
      return;
    }

    for (const item of emailsForm) {
      if (!item.email.trim()) {
        warningNotification(
          'Atenção!',
          'Preencha todos os emails adicionais ou remova as linhas vazias.'
        );
        return;
      }
      if (!isValidEmail(item.email.trim())) {
        warningNotification('Atenção!', 'E-mail inválido: ' + item.email);
        return;
      }
    }

    const notificationRows = emailsForm.flatMap(item =>
      item.categories
        .filter(cat => VALID_CATEGORIES.includes(cat))
        .map(cat => ({ email: item.email.trim(), category: cat }))
    );

    const payload = [
      { email: mainEmailForm.trim(), category: 'MAIN' },
      ...notificationRows
    ];

    setSavingEmails(true);
    try {
      await put(`/estates/update-emails/${estateId}`, payload);
      successNotification('Sucesso!', 'E-mails atualizados com sucesso!');
      setEditingEmails(false);
      await fetchEstate();
    } catch {
      dangerNotification('Erro!', 'Não foi possível atualizar os e-mails.');
    } finally {
      setSavingEmails(false);
    }
  };

  const handleAddEmail = () => {
    setEmailsForm(prev => [...prev, { email: '', categories: [] }]);
  };

  const handleRemoveEmail = (index: number) => {
    setEmailsForm(prev => prev.filter((_, i) => i !== index));
  };

  const handleEmailChange = (index: number, value: string) => {
    setEmailsForm(prev => {
      const next = [...prev];
      next[index] = { ...next[index], email: value };
      return next;
    });
  };

  const handleCategoryChange = (index: number, selected: string[]) => {
    const valid = selected.filter(c => VALID_CATEGORIES.includes(c));
    setEmailsForm(prev => {
      const next = [...prev];
      next[index] = { ...next[index], categories: valid };
      return next;
    });
  };

  // ---------------------------------------------------------------------------

  const hasPartners = (estate?.partners?.length ?? 0) > 0;

  // ---------------------------------------------------------------------------
  // Tabs
  // ---------------------------------------------------------------------------

  const tabPanes = [
    {
      menuItem: 'Dados Gerais',
      render: () => (
        <Tab.Pane>
          <TabContent>
            <Section>
              <SectionHeader>
                <SectionTitle>Identificação</SectionTitle>
              </SectionHeader>
              <SectionDivider />
              <FieldGrid>
                <Field>
                  <FieldLabel>E-mail Principal</FieldLabel>
                  <FieldValue>{estate?.email || '-'}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Nome da Imobiliária</FieldLabel>
                  <FieldValue>{estate?.name || '-'}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Razão Social</FieldLabel>
                  <FieldValue>{estate?.company_name || '-'}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>CNPJ</FieldLabel>
                  <FieldValue>
                    {estate?.cnpj ? maskCpfOrCnpj(estate.cnpj) : '-'}
                  </FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Data de Aniversário</FieldLabel>
                  <FieldValue>
                    {estate?.birthday ? DateToStringDate(new Date(estate.birthday)) : '-'}
                  </FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Gestor de Contas</FieldLabel>
                  <FieldValue>{estate?.user?.name || '-'}</FieldValue>
                </Field>
              </FieldGrid>
            </Section>
          </TabContent>
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Responsáveis',
      render: () => (
        <Tab.Pane>
          <TabContent>
            <Section>
              <SectionHeader>
                <SectionTitle>Responsável</SectionTitle>
              </SectionHeader>
              <SectionDivider />
              <FieldGrid>
                <Field>
                  <FieldLabel>Nome do Responsável</FieldLabel>
                  <FieldValue>{estate?.responsible_name || '-'}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Email do Responsável</FieldLabel>
                  <FieldValue>{estate?.responsible_email || '-'}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>CPF do Responsável</FieldLabel>
                  <FieldValue>
                    {estate?.responsible_document
                      ? maskCpf(estate.responsible_document)
                      : '-'}
                  </FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Telefone do Responsável</FieldLabel>
                  <FieldValue>
                    {estate?.responsible_phone
                      ? maskPhoneNumber(estate.responsible_phone)
                      : '-'}
                  </FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Responsável Financeiro</FieldLabel>
                  <FieldValue>{estate?.financial_responsible || '-'}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Email do Financeiro</FieldLabel>
                  <FieldValue>{estate?.financial_email || '-'}</FieldValue>
                </Field>
              </FieldGrid>
            </Section>

            {hasPartners && (
              <Section>
                <SectionHeader>
                  <SectionTitle>Quadro de Sócios</SectionTitle>
                </SectionHeader>
                <SectionDivider />
                {estate!.partners!.map((partner, index) => (
                  <PartnerCard key={partner.id || index}>
                    <FieldGrid cols={partner.email ? 3 : 2}>
                      <Field>
                        <FieldLabel>Nome</FieldLabel>
                        <FieldValue>{partner.name || '-'}</FieldValue>
                      </Field>
                      <Field>
                        <FieldLabel>CPF</FieldLabel>
                        <FieldValue>
                          {partner.document ? maskCpf(partner.document) : '-'}
                        </FieldValue>
                      </Field>
                      {partner.email && (
                        <Field>
                          <FieldLabel>Email</FieldLabel>
                          <FieldValue>{partner.email}</FieldValue>
                        </Field>
                      )}
                    </FieldGrid>
                  </PartnerCard>
                ))}
              </Section>
            )}
          </TabContent>
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Endereço & Contato',
      render: () => (
        <Tab.Pane>
          <Dimmer active={savingAddress || savingEmails} inverted>
            <Loader>Salvando...</Loader>
          </Dimmer>
          <TabContent>
            {/* ── Endereço ── */}
            <Section>
              <SectionHeader>
                <SectionTitle>Endereço da Imobiliária</SectionTitle>
                {!editingAddress && (
                  <EditButton type="button" onClick={handleStartEditAddress}>
                    <Icon name="pencil" /> Editar
                  </EditButton>
                )}
              </SectionHeader>
              <SectionDivider />

              {!editingAddress ? (
                <FieldGrid>
                  <Field>
                    <FieldLabel>CEP</FieldLabel>
                    <FieldValue>{estate?.estate_zip_code || '-'}</FieldValue>
                  </Field>
                  <Field>
                    <FieldLabel>Logradouro</FieldLabel>
                    <FieldValue>{estate?.estate_street || '-'}</FieldValue>
                  </Field>
                  <Field>
                    <FieldLabel>Bairro</FieldLabel>
                    <FieldValue>{estate?.estate_district || '-'}</FieldValue>
                  </Field>
                  <Field>
                    <FieldLabel>Cidade</FieldLabel>
                    <FieldValue>{estate?.estate_city || '-'}</FieldValue>
                  </Field>
                  <Field>
                    <FieldLabel>UF</FieldLabel>
                    <FieldValue>{estate?.estate_state || '-'}</FieldValue>
                  </Field>
                  <Field>
                    <FieldLabel>Número</FieldLabel>
                    <FieldValue>{estate?.estate_number || '-'}</FieldValue>
                  </Field>
                  <Field style={{ gridColumn: '1 / -1' }}>
                    <FieldLabel>Complemento</FieldLabel>
                    <FieldValue>{estate?.estate_complement || '-'}</FieldValue>
                  </Field>
                </FieldGrid>
              ) : (
                <>
                  <FieldGrid>
                    <Field>
                      <FieldLabel>CEP</FieldLabel>
                      <Input
                        size="small"
                        placeholder="00000-000"
                        value={addressForm.estate_zipcode}
                        onChange={(_, { value }) =>
                          updateAddressField('estate_zipcode', value)
                        }
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Logradouro</FieldLabel>
                      <Input
                        size="small"
                        placeholder="Rua, Av..."
                        value={addressForm.estate_street}
                        onChange={(_, { value }) =>
                          updateAddressField('estate_street', value)
                        }
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Bairro</FieldLabel>
                      <Input
                        size="small"
                        placeholder="Bairro"
                        value={addressForm.estate_district}
                        onChange={(_, { value }) =>
                          updateAddressField('estate_district', value)
                        }
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Cidade</FieldLabel>
                      <Input
                        size="small"
                        placeholder="Cidade"
                        value={addressForm.estate_city}
                        onChange={(_, { value }) =>
                          updateAddressField('estate_city', value)
                        }
                      />
                    </Field>
                    <Field>
                      <FieldLabel>UF</FieldLabel>
                      <Input
                        size="small"
                        placeholder="UF"
                        maxLength={2}
                        value={addressForm.estate_state}
                        onChange={(_, { value }) =>
                          updateAddressField('estate_state', value.toUpperCase())
                        }
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Número</FieldLabel>
                      <Input
                        size="small"
                        placeholder="Nº"
                        value={addressForm.estate_number}
                        onChange={(_, { value }) =>
                          updateAddressField('estate_number', value)
                        }
                      />
                    </Field>
                    <Field style={{ gridColumn: '1 / -1' }}>
                      <FieldLabel>Complemento</FieldLabel>
                      <Input
                        size="small"
                        placeholder="Sala, andar, bloco..."
                        value={addressForm.estate_complement}
                        onChange={(_, { value }) =>
                          updateAddressField('estate_complement', value)
                        }
                      />
                    </Field>
                  </FieldGrid>
                  <EditActions>
                    <Button
                      size="small"
                      basic
                      color="grey"
                      onClick={handleCancelEditAddress}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="small"
                      color="violet"
                      loading={savingAddress}
                      disabled={savingAddress}
                      onClick={handleSaveAddress}
                    >
                      <Icon name="save" /> Salvar
                    </Button>
                  </EditActions>
                </>
              )}
            </Section>

            {/* ── Emails ── */}
            <Section>
              <SectionHeader>
                <SectionTitle>Emails</SectionTitle>
                {!editingEmails && (
                  <EditButton type="button" onClick={handleStartEditEmails}>
                    <Icon name="pencil" /> Editar
                  </EditButton>
                )}
              </SectionHeader>
              <SectionDivider />

              {!editingEmails ? (
                <>
                  {/* E-mail principal em destaque */}
                  <MainEmailField>
                    <MainEmailLabel>E-mail Principal</MainEmailLabel>
                    <EmailAddress>
                      <Icon className="icon" name="mail outline" />
                      {estate?.email || '-'}
                    </EmailAddress>
                  </MainEmailField>

                  {/* Emails de notificação */}
                  <SubSectionLabel>Emails de Notificação</SubSectionLabel>
                  {groupedEmails.length === 0 ? (
                    <EmptyText>Nenhum email de notificação cadastrado.</EmptyText>
                  ) : (
                    <EmailList>
                      {groupedEmails.map((item, index) => (
                        <EmailRow key={index}>
                          <EmailAddress>
                            <Icon className="icon" name="mail outline" />
                            {item.email}
                          </EmailAddress>
                          {item.categories.length > 0 && (
                            <CategoryTagsGroup>
                              {item.categories.map(cat => {
                                const colors = DEFAULT_CATEGORY_COLOR;
                                return (
                                  <CategoryTag
                                    key={cat}
                                    color={colors.color}
                                    bg={colors.bg}
                                  >
                                    {CATEGORY_LABELS[cat] ?? cat}
                                  </CategoryTag>
                                );
                              })}
                            </CategoryTagsGroup>
                          )}
                        </EmailRow>
                      ))}
                    </EmailList>
                  )}
                </>
              ) : (
                <>
                  {/* E-mail principal editável */}
                  <SubSectionLabel>E-mail Principal *</SubSectionLabel>
                  <Input
                    fluid
                    size="small"
                    placeholder="email@imobiliaria.com.br"
                    value={mainEmailForm}
                    onChange={(_, { value }) => setMainEmailForm(value)}
                    style={{ marginBottom: 20 }}
                  />

                  {/* Emails de notificação editáveis */}
                  <SubSectionLabel>Emails de Notificação</SubSectionLabel>
                  {emailsForm.map((item, index) => (
                    <EditEmailRow key={index}>
                      <Input
                        size="small"
                        placeholder="email@exemplo.com"
                        value={item.email}
                        onChange={(_, { value }) => handleEmailChange(index, value)}
                        fluid
                      />
                      <Dropdown
                        multiple
                        selection
                        clearable
                        fluid
                        size="small"
                        placeholder="Categorias"
                        options={CATEGORY_OPTIONS}
                        value={item.categories}
                        onChange={(_, { value }) =>
                          handleCategoryChange(index, value as string[])
                        }
                      />
                      <Icon
                        name="times circle"
                        color="grey"
                        size="large"
                        style={{ cursor: 'pointer', margin: 0 }}
                        onClick={() => handleRemoveEmail(index)}
                      />
                    </EditEmailRow>
                  ))}

                  <AddEmailButton type="button" onClick={handleAddEmail}>
                    <Icon name="plus circle" /> Adicionar e-mail
                  </AddEmailButton>

                  <EditActions>
                    <Button
                      size="small"
                      basic
                      color="grey"
                      onClick={handleCancelEditEmails}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="small"
                      color="violet"
                      loading={savingEmails}
                      disabled={savingEmails}
                      onClick={handleSaveEmails}
                    >
                      <Icon name="save" /> Salvar
                    </Button>
                  </EditActions>
                </>
              )}
            </Section>
          </TabContent>
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Seguradoras Ativas',
      render: () => (
        <Tab.Pane>
          <TabContent>
            <Section>
              <SectionHeader>
                <SectionTitle>Seguradoras Ativas</SectionTitle>
              </SectionHeader>
              <SectionDivider />
              {insurersDataFormalized.length === 0 ? (
                <EmptyText>Nenhuma seguradora ativa.</EmptyText>
              ) : (
                <InsurerGrid>
                  <Grid.Row style={insurerGridStyles.Header}>
                    <Grid.Column width={6}>
                      <b>SEGURADORA</b>
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <b>SEGURO INCÊNDIO</b>
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <b>TÍTULO DE CAPITALIZAÇÃO</b>
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <b>SEGURO FIANÇA</b>
                    </Grid.Column>
                  </Grid.Row>
                  {insurersDataFormalized.map(insurerData => (
                    <Grid.Row key={insurerData.id} style={insurerGridStyles.Row}>
                      <Grid.Column
                        width={6}
                        style={insurerGridStyles.InsurerInfoContainer}
                      >
                        {insurerData.image && (
                          <img
                            src={insurerData.image}
                            className="logo"
                            alt="Insurer logo"
                          />
                        )}
                        <div className="insurer-content">
                          <span className="insurer-name">{insurerData.name}</span>
                        </div>
                      </Grid.Column>
                      <Grid.Column width={3} style={insurerGridStyles.IconContainer}>
                        {insurerData.hasFireInsurance ? (
                          <IconButton
                            name="check circle"
                            style={insurerGridStyles.EnabledIcon}
                            size="large"
                          />
                        ) : (
                          <Icon
                            name="stop circle"
                            style={insurerGridStyles.DisabledIcon}
                            size="large"
                          />
                        )}
                      </Grid.Column>
                      <Grid.Column width={4} style={insurerGridStyles.IconContainer}>
                        {insurerData.hasCapitalization ? (
                          <IconButton
                            name="check circle"
                            style={insurerGridStyles.EnabledIcon}
                            size="large"
                          />
                        ) : (
                          <Icon
                            name="stop circle"
                            style={insurerGridStyles.DisabledIcon}
                            size="large"
                          />
                        )}
                      </Grid.Column>
                      <Grid.Column width={3} style={insurerGridStyles.IconContainer}>
                        {insurerData.hasInsurance ? (
                          <IconButton
                            name="check circle"
                            style={insurerGridStyles.EnabledIcon}
                            size="large"
                          />
                        ) : (
                          <Icon
                            name="stop circle"
                            style={insurerGridStyles.DisabledIcon}
                            size="large"
                          />
                        )}
                      </Grid.Column>
                    </Grid.Row>
                  ))}
                </InsurerGrid>
              )}
            </Section>
          </TabContent>
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Dados Bancários',
      render: () => (
        <Tab.Pane>
          <TabContent>
            <Dimmer active={savingBilling} inverted>
              <Loader>Salvando...</Loader>
            </Dimmer>
            <Section>
              <SectionHeader>
                <SectionTitle>Dados Bancários</SectionTitle>
              </SectionHeader>
              <SectionDivider />
              {!estate?.bank_account ? (
                <EmptyText>Nenhum dado bancário cadastrado.</EmptyText>
              ) : (
                <>
                  <FieldGrid>
                    <Field>
                      <FieldLabel>Banco</FieldLabel>
                      <FieldValue>
                        {bankSelected?.text || estate.bank_account.bank_code || '-'}
                      </FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Agência</FieldLabel>
                      <FieldValue>
                        {estate.bank_account.agency_number}
                        {estate.bank_account.agency_dv
                          ? `-${estate.bank_account.agency_dv}`
                          : ''}
                      </FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Conta</FieldLabel>
                      <FieldValue>
                        {estate.bank_account.account_number}
                        {estate.bank_account.account_dv
                          ? `-${estate.bank_account.account_dv}`
                          : ''}
                      </FieldValue>
                    </Field>
                  </FieldGrid>
                  <Disclaimer>
                    <Icon className="icon" name="exclamation circle" />
                    <p>
                      Confira as informações acima e caso encontre divergências, entre em
                      contato com o seu gestor de contas ou atendimento para que seja
                      feita a devida correção.
                    </p>
                  </Disclaimer>
                </>
              )}
            </Section>

            {canViewBillingConfiguration &&
              (canManageBillingConfiguration || chargesEnabled) && (
                <Section>
                  <SectionHeader>
                    <SectionTitle>Informações de Cobrança</SectionTitle>
                  </SectionHeader>
                  <SectionDivider />
                  <p style={{ color: '#666' }}>
                    A conta conectada recebe os valores das cobranças realizadas pelo
                    painel.
                  </p>
                  {canManageBillingConfiguration && (
                    <div style={{ display: 'flex', gap: 32, marginBottom: 20 }}>
                      <div>
                        <FieldLabel>Habilitar envio de cobranças</FieldLabel>
                        <Checkbox
                          toggle
                          checked={chargesEnabled}
                          disabled={savingBillingSettings}
                          onChange={(_, data) =>
                            handleUpdateBillingSettings(
                              Boolean(data.checked),
                              Boolean(estate?.standalone_charges_commission_enabled)
                            )
                          }
                        />
                      </div>
                      {chargesEnabled && (
                        <div>
                          <FieldLabel>Habilitar comissionamento</FieldLabel>
                          <Checkbox
                            toggle
                            checked={Boolean(
                              estate?.standalone_charges_commission_enabled
                            )}
                            disabled={savingBillingSettings}
                            onChange={(_, data) =>
                              handleUpdateBillingSettings(true, Boolean(data.checked))
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {chargesEnabled && (
                    <FieldGrid>
                      <Field>
                        <FieldLabel>Conta bancária</FieldLabel>
                        <FieldValue>
                          {accountLoading
                            ? 'Consultando conta...'
                            : connected
                            ? 'Conta conectada.'
                            : 'Nenhuma conta atribuída.'}
                        </FieldValue>
                        {!accountLoading && !connected && (
                          <Button
                            size="small"
                            color="violet"
                            onClick={() => {
                              onClose();
                              history.push(
                                `/admin/standalone-charges?estateId=${estateId}`
                              );
                            }}
                          >
                            Conectar
                          </Button>
                        )}
                      </Field>
                      <Field>
                        <FieldLabel>Taxa de operação (%)</FieldLabel>
                        <FieldValue>
                          {estate?.standalone_charges_split_percent !== undefined &&
                          estate?.standalone_charges_split_percent !== null
                            ? `${Number(estate.standalone_charges_split_percent).toFixed(
                                2
                              )}%`
                            : '-'}
                        </FieldValue>
                      </Field>
                      {estate?.standalone_charges_commission_enabled && (
                        <Field>
                          <FieldLabel>Repasse ao colaborador</FieldLabel>
                          <FieldValue>
                            {estate.standalone_charges_commission_percent !== undefined &&
                            estate.standalone_charges_commission_percent !== null
                              ? `${Number(
                                  estate.standalone_charges_commission_percent
                                ).toFixed(2)}%`
                              : 'Habilitado'}
                          </FieldValue>
                          <p style={{ margin: 0, fontSize: '0.8em', color: '#666' }}>
                            Aplicado aos colaboradores válidos vinculados às cobranças.
                          </p>
                        </Field>
                      )}
                    </FieldGrid>
                  )}
                </Section>
              )}
          </TabContent>
        </Tab.Pane>
      )
    }
  ];

  return (
    <Modal open={isOpen} onClose={onClose} closeIcon size="large">
      <Modal.Header style={{ borderBottom: '3px solid #59249B' }}>
        {loading ? 'Carregando...' : estate?.name || 'Dados Cadastrais'}
      </Modal.Header>
      <Modal.Content scrolling style={{ padding: '0 24px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#AAADB1' }}>
            <Icon name="spinner" loading size="large" />
            <p style={{ marginTop: 14, fontSize: 14 }}>Carregando dados cadastrais...</p>
          </div>
        ) : (
          <Tab
            panes={tabPanes}
            menu={{ secondary: true, pointing: true }}
            style={{ marginTop: 8 }}
          />
        )}
      </Modal.Content>
      <Modal.Actions style={{ backgroundColor: '#f8f9fa' }}>
        <Button color="grey" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default DadosCadastraisModal;
